import React, {useEffect} from "react";
import dateFormat, { i18n } from "dateformat";
import { useSelector } from "react-redux";
import {useLocation, useNavigate} from "react-router-dom";

import { useAppDispatch, useAppSelector } from "@redux/store";
import { rentSelector } from "@redux/rent/selectors";
import { fetchGetArenas, fetchCreateCancelRent } from "@redux/rent/asyncActions";
import { ISelctedSeat } from "@redux/rent/types";
import { prepareCancelReserveForReOrder, isReOrderOperation } from "@/utils/courtRent/reorder";
import { setShowWindow } from "@redux/profile/authAndReg/slice";
import {
  resetSelectedSeats,
  setDate,
  resetSelectedRackets,
} from "@redux/rent/slice";

import {
  Button,
  Modal,
  Header,
  Title,
} from "@/components";

import styles from "./CourtRentTime.module.scss";

import {
  getRentDateFromStorage,
  setCartItemsToStorage,
  getCartItemsFromStorage,
  dateRestFormat,
} from "@/utils";

import { dayNamesForRent, ModalTexts } from "@resources/locale";
import { WindowNames } from "@resources/constants";

import {CourtRentTimePropsType} from "./types";
import { moscowNow } from "@/utils/date/moscowNow";
import { openModal } from "react-url-modal";
import {getReserveGrunts} from "@redux/profile/grunt/selectors";
import {setGruntAvailable} from "@redux/profile/grunt/slice";
import { TableCourts } from "../components/TableCourts";
import { WeekDays } from "../components/WeekDays";
import { DateNavigation } from "../components/DateNavigation";
import { PromoModals } from "../components/PromoModals";
import { checkAndShowPromoModal, markPromoModalAsShown } from "@/utils/courtRent/promoModals";
import { sortArenaTitles } from "@/utils/courtRent/arenaNames";

i18n.dayNames = dayNamesForRent;

export const CourtRentTime: React.FC<CourtRentTimePropsType> = ({ title }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const tableRef = React.useRef<HTMLTableElement | null>(null);

  const { pathname, state } = useLocation();

  const { auth } = useAppSelector(state => state.authAndReg);
  const { date, arenas, selectedSeats, isLoading, isBackgroundLoading } = useSelector(rentSelector);
  const {gruntsAvailable, gruntArenas, grunt_id, items: gruntItems} = useSelector(getReserveGrunts);

  // Определяем активные арены (обычные или grunt)
  const activeArenas = React.useMemo(() => {
    return gruntsAvailable ? gruntArenas : arenas;
  }, [gruntsAvailable, gruntArenas, arenas]);

  // Просматриваемая дата (для навигации по месяцам, без выбора дня)
  const [viewedDate, setViewedDate] = React.useState<string>("");

  const [tableWidth, setTableWidth] = React.useState<number>(2500);
  const [isOpenModalUnavailable, setIsOpenModalUnavailable] =
    React.useState<boolean>(false);
  const [isOpenModalNoSelectedSeats, setIsOpenModalNoSelectedSeats] =
    React.useState<boolean>(false);
  
  const [showWeekdayPromoModal, setShowWeekdayPromoModal] = React.useState<boolean>(false);
  const [showWeekendPromoModal1, setShowWeekendPromoModal1] = React.useState<boolean>(false);
  const [showWeekendPromoModal2, setShowWeekendPromoModal2] = React.useState<boolean>(false);
  
  const [isProcessingBasket, setIsProcessingBasket] = React.useState<boolean>(false);
  const [selectedArenaTitle, setSelectedArenaTitle] = React.useState<string | null>(
    (state as any)?.selectedArenaTitle || null
  );
  const [activeArenaTab, setActiveArenaTab] = React.useState<string | null>(null);
  const activeArenaTabRef = React.useRef<string | null>(null);
  const isScrollingProgrammatically = React.useRef(false);

  const arenaTypes = React.useMemo(() => {
    const uniqueTypes = new Set<string>();
    activeArenas.forEach((arena) => {
      if (arena.title) {
        uniqueTypes.add(arena.title);
      }
    });

    return sortArenaTitles(Array.from(uniqueTypes));
  }, [activeArenas]);

  // Создаем маппинг количества кортов по типу арены
  const arenaCourtCountMap = React.useMemo(() => {
    const countMap: { [key: string]: number } = {};
    activeArenas.forEach((arena) => {
      if (arena.title) {
        countMap[arena.title] = (countMap[arena.title] || 0) + (arena.courts?.length || 0);
      }
    });
    return countMap;
  }, [activeArenas]);

  // Создаем маппинг названий арен к их цветам категорий
  const arenaColorMap = React.useMemo(() => {
    const colorMap: { [key: string]: string } = {};
    activeArenas.forEach((arena) => {
      if (arena.title && arena.cell_color) {
        colorMap[arena.title] = arena.cell_color;
      }
    });
    return colorMap;
  }, [activeArenas]);

  const tableWrapperRef = React.useRef<HTMLDivElement>(null);
  const arenaTabsRef = React.useRef<HTMLDivElement>(null);
  const stickyControlsRef = React.useRef<HTMLDivElement>(null);

  const scrollToArena = React.useCallback((arenaTitle: string) => {
    if (!tableWrapperRef.current) return;

    isScrollingProgrammatically.current = true;

    setTimeout(() => {
      if (!tableWrapperRef.current) {
        isScrollingProgrammatically.current = false;
        return;
      }

      const arenaElement = tableWrapperRef.current.querySelector(
        `th[data-arena-title="${arenaTitle}"]`
      ) as HTMLElement;

      if (arenaElement && tableWrapperRef.current) {
        const containerRect = tableWrapperRef.current.getBoundingClientRect();
        const elementRect = arenaElement.getBoundingClientRect();
        const scrollLeft = tableWrapperRef.current.scrollLeft + (elementRect.left - containerRect.left);

        tableWrapperRef.current.scrollTo({
          left: scrollLeft,
          behavior: 'smooth'
        });

        // Снимаем блокировку после завершения smooth scroll анимации
        setTimeout(() => {
          isScrollingProgrammatically.current = false;
        }, 600);
      } else {
        isScrollingProgrammatically.current = false;
      }
    }, 100);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  useEffect(() => {
    const storageDate = getRentDateFromStorage();
    if (storageDate) dispatch(setDate(storageDate));
    if (!storageDate) dispatch(setDate(dateFormat(moscowNow(), "isoDate")));

    dispatch(resetSelectedRackets());
    window.addEventListener("resize", calcTableWidth);

    return () => {
      window.removeEventListener("resize", calcTableWidth);
    };
  }, []);

  useEffect(() => {
    return function cleanGruntState() {
      dispatch(setGruntAvailable(false));
    }
  }, [])

  // Синхронизируем viewedDate при изменении выбранного дня
  useEffect(() => {
    if (date) {
      setViewedDate(date);
    }
  }, [date]);

  useEffect(() => {
    if (date) {
      // Используем дефолтный partnerId, если не найден в профиле
      dispatch(fetchGetArenas({ date }));
    }
  }, [date, dispatch]);

  const handleMonthChange = (newViewedDate: string) => {
    const newDate = new Date(Date.parse(newViewedDate));
    const today = moscowNow();
    today.setHours(0, 0, 0, 0);

    // Если переходим на текущий месяц — центрируем на сегодня
    if (newDate.getMonth() === today.getMonth() && newDate.getFullYear() === today.getFullYear()) {
      setViewedDate(dateFormat(today, "isoDate"));
    } else {
      setViewedDate(newViewedDate);
    }
  };

  // Обработчик выбора слота для проверки промо-модальных окон
  const handleSeatSelect = React.useCallback((cellItem: { time: string; courtType?: string; bitrixCourtId?: string }) => {
    const result = checkAndShowPromoModal(cellItem, date, selectedSeats);
    
    if (result.shouldShow && result.modalKey) {
      // Помечаем модальное окно как показанное
      markPromoModalAsShown(result.modalKey);
      
      // Показываем соответствующее модальное окно
      switch (result.modalKey) {
        case 'padel_weekday_modal':
          setShowWeekdayPromoModal(true);
          break;
        case 'padel_weekend_modal':
          setShowWeekendPromoModal1(true);
          break;
        case 'hard_weekend_modal':
          setShowWeekendPromoModal2(true);
          break;
      }
    }
  }, [date, selectedSeats]);

  useEffect(() => {
    calcTableWidth();
  }, [arenas]);

  // Скролл к выбранной арене при переходе из ChoosingRoom
  useEffect(() => {
    if (selectedArenaTitle && !isLoading && !isBackgroundLoading && arenas.length > 0 && tableWrapperRef.current) {
      // Задержка для гарантии, что таблица отрендерена
      const timer = setTimeout(() => {
        scrollToArena(selectedArenaTitle);
        // Устанавливаем активный таб и сбрасываем selectedArenaTitle после скролла
        setActiveArenaTab(selectedArenaTitle);
        setSelectedArenaTitle(null);
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [selectedArenaTitle, isLoading, isBackgroundLoading, arenas.length, scrollToArena]);

  // Автоматический скролл полоски табов к активному табу
  useEffect(() => {
    if (!activeArenaTab || !arenaTabsRef.current) return;

    const activeButton = arenaTabsRef.current.querySelector(
      `button.${styles.arenaTab_active}`
    ) as HTMLElement;

    if (activeButton) {
      const container = arenaTabsRef.current;
      const containerRect = container.getBoundingClientRect();
      const buttonRect = activeButton.getBoundingClientRect();

      // Если кнопка не полностью видна — подскроллить
      if (buttonRect.left < containerRect.left || buttonRect.right > containerRect.right) {
        const scrollLeft = container.scrollLeft + (buttonRect.left - containerRect.left) - (containerRect.width / 2 - buttonRect.width / 2);
        container.scrollTo({
          left: scrollLeft,
          behavior: 'smooth'
        });
      }
    }
  }, [activeArenaTab]);

  // Синхронизируем ref с state
  useEffect(() => {
    activeArenaTabRef.current = activeArenaTab;
  }, [activeArenaTab]);

  // Автоматическое переключение активного таба на основе видимой арены при скролле
  useEffect(() => {
    if (!tableWrapperRef.current || !arenaTypes.length || isLoading || isBackgroundLoading) return;

    const container = tableWrapperRef.current;
    let scrollTimeout: ReturnType<typeof setTimeout> | null = null;

    const updateActiveTab = () => {
      if (!container || isScrollingProgrammatically.current) return;

      const containerRect = container.getBoundingClientRect();

      let closestArenaTitle: string | null = null;
      let minDistance = Infinity;

      arenaTypes.forEach((arenaTitle) => {
        const arenaElements = container.querySelectorAll(
          `th[data-arena-title="${arenaTitle}"]`
        );

        arenaElements.forEach((arenaElement) => {
          const elementRect = arenaElement.getBoundingClientRect();

          // Пропускаем полностью невидимые элементы
          if (elementRect.right <= containerRect.left || elementRect.left >= containerRect.right) return;

          // Ищем арену, чей левый край ближе всего к левому краю контейнера
          const distance = Math.abs(elementRect.left - containerRect.left);
          if (distance < minDistance) {
            minDistance = distance;
            closestArenaTitle = arenaTitle;
          }
        });
      });

      if (closestArenaTitle && closestArenaTitle !== activeArenaTabRef.current) {
        setActiveArenaTab(closestArenaTitle);
      }
    };

    const handleScroll = () => {
      if (isScrollingProgrammatically.current) return;
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
      scrollTimeout = setTimeout(updateActiveTab, 50);
    };

    container.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleScroll);
    // Первоначальная проверка
    updateActiveTab();

    return () => {
      container.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
    };
  }, [tableWrapperRef, arenaTypes, isLoading, isBackgroundLoading]);

  // Вычисляем позицию для sticky thead (ниже stickyControls)
  useEffect(() => {
    const el = stickyControlsRef.current;
    if (!el) return;
    const wrapper = el.parentElement;
    if (!wrapper) return;

    const update = () => {
      const computedTop = parseFloat(getComputedStyle(el).top) || 0;
      const height = el.offsetHeight;
      wrapper.style.setProperty('--thead-top', `${computedTop + height}px`);
    };

    const observer = new ResizeObserver(update);
    observer.observe(el);
    update();
    window.addEventListener('resize', update);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', update);
    };
  }, []);

  function calcTableWidth() {
    const positionInfo = tableRef.current?.getBoundingClientRect();
    const width = positionInfo?.width;
    if (width) setTableWidth((tableWidth) => (tableWidth = 2500));
    setTimeout(() => {
      if (width) setTableWidth((tableWidth) => (tableWidth = width));
    }, 500);
  }

  const handleCloseModalNoSeats = () => {
    setIsOpenModalNoSelectedSeats(false);
  };

  const handleCloseModalUnavailable = () => {
    setIsOpenModalUnavailable(false);
    dispatch(fetchGetArenas({ date }));
    dispatch(resetSelectedSeats());
  };

  const handleAddToCart = async () => {
    if (Object.keys(selectedSeats).length === 0) {
      setIsOpenModalNoSelectedSeats(true);
      return;
    }
    if (!auth) {
      openModal({name:"login",params:{
        showWindow:'auth'
      }})
      dispatch(setShowWindow(WindowNames.AUTH));
      return;
    }
    if (isProcessingBasket) return;
    setIsProcessingBasket(true);
    {
      try {
        // Проверяем, является ли это перебронированием
        const isReOrder = isReOrderOperation(grunt_id);
        let cancelBasket: { [key: string]: ISelctedSeat } = {};

        // Если это перебронирование, подготавливаем данные для отмены старого бронирования
        if (isReOrder && grunt_id !== null && gruntItems.length > 0) {
          // Находим старое бронирование по grunt_id
          const oldReservation = gruntItems.find(item => item.id === grunt_id);
          
          if (oldReservation) {
            // Ищем bitrixCourtId в активных кортах
            let bitrixCourtId = '';
            for (const arena of arenas) {
              const court = arena.courts.find((c: any) => c.id === oldReservation.court_id);
              if (court && (court as any).courtId) {
                bitrixCourtId = (court as any).courtId;
                break;
              }
            }
            
            // Если не нашли в arenas, пробуем в gruntArenas
            if (!bitrixCourtId && gruntArenas.length > 0) {
              for (const arena of gruntArenas) {
                const court = arena.courts.find((c: any) => c.id === oldReservation.court_id);
                if (court && (court as any).courtId) {
                  bitrixCourtId = (court as any).courtId;
                  break;
                }
              }
            }
            
            // Fallback на court_id как строку
            if (!bitrixCourtId) {
              bitrixCourtId = oldReservation.court_id?.toString() || '';
            }
            
            // Преобразуем старое бронирование в формат для отмены
            const cancelKey = `${oldReservation.id}-${oldReservation.court_id}-${oldReservation.arena_id}-${oldReservation.date}`;
            
            cancelBasket[cancelKey] = {
              arenaTitle: (oldReservation.arena as any)?.title || '',
              arenaId: oldReservation.arena_id,
              courtTitle: oldReservation.court?.title || '',
              courtId: oldReservation.court_id,
              court_id: oldReservation.court_id,
              id: oldReservation.id,
              date: oldReservation.date,
              time: oldReservation.time,
              price: oldReservation.price,
              price_weekend: oldReservation.price,
              is_special_price: false,
              startTimeCourt: '',
              bitrixCourtId: bitrixCourtId,
            };
          }
        }

        // Создаем бронирование через Bitrix API
        const result = await dispatch(fetchCreateCancelRent({
          newBasket: selectedSeats,
          cancelBasket: cancelBasket,
        })).unwrap();

        // Если бронирование успешно создано, добавляем в корзину
        const dateAddedToCart = dateRestFormat(new Date());
        const cloneSelectedSeats = JSON.parse(JSON.stringify(selectedSeats));

        for (let key in cloneSelectedSeats) {
          cloneSelectedSeats[key].startTimeCourt = dateAddedToCart;
        }

        let seatsStorage = {};
        let racketsStorage = {};
        const currentStorageCartItems = getCartItemsFromStorage();

        if (currentStorageCartItems) {
          seatsStorage = currentStorageCartItems.seats;
          racketsStorage = currentStorageCartItems.rackets;
        }

        const cartItemsForStorage = {
          rackets: { ...racketsStorage },
          seats: { ...seatsStorage, ...cloneSelectedSeats },
        };

        setCartItemsToStorage(cartItemsForStorage);
        dispatch(resetSelectedSeats());
        navigate("/cart");
      } catch (error: any) {
        console.error("Error creating reservation:", error);
        setIsOpenModalUnavailable(true);
      } finally {
        setIsProcessingBasket(false);
      }
    }
  };

  return (
    <>
      <Header title={title} />
      <main className={styles.main}>
        <section className={`container ${styles.section}`}>
          <Title>{title}</Title>

          <p className={styles.subTitle}>
            Выберите корт и время
          </p>

          <div className={styles.wrapper}>
            <div className={styles.stickyControls} ref={stickyControlsRef}>
              <DateNavigation viewedDate={viewedDate} onMonthChange={handleMonthChange} />

              <WeekDays date={viewedDate} selectedDate={date} />

              {arenaTypes.length > 1 && (
                <div className={styles.arenaTabs} ref={arenaTabsRef}>
                  {arenaTypes.map((arenaTitle) => {
                    const categoryColor = arenaColorMap[arenaTitle] || undefined;
                    const count = arenaCourtCountMap[arenaTitle] || 0;
                    const getCourtWord = (n: number) => {
                      const last = n % 10;
                      const lastTwo = n % 100;
                      if (lastTwo >= 11 && lastTwo <= 14) return 'кортов';
                      if (last === 1) return 'корт';
                      if (last >= 2 && last <= 4) return 'корта';
                      return 'кортов';
                    };
                    const lowerTitle = arenaTitle.toLowerCase();
                    const displayTitle = lowerTitle.includes('padel')
                      ? (lowerTitle.includes('улиц') ? 'Padel (улица)' : 'Padel')
                      : arenaTitle;
                    return (
                      <button
                        key={arenaTitle}
                        className={`${styles.arenaTab} ${
                          activeArenaTab === arenaTitle ? styles.arenaTab_active : ""
                        }`}
                        style={
                          activeArenaTab === arenaTitle && categoryColor
                            ? { borderBottomColor: categoryColor }
                            : undefined
                        }
                        onClick={() => {
                          setActiveArenaTab(arenaTitle);
                          scrollToArena(arenaTitle);
                        }}
                      >
                        {displayTitle}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className={styles.tableArea}>
              <TableCourts
                gruntsAvailable={gruntsAvailable}
                arenas={arenas}
                gruntArenas={gruntArenas}
                grunt_id={grunt_id}
                isLoading={isLoading}
                tableWidth={tableWidth}
                tableRef={tableRef}
                tableWrapperRef={tableWrapperRef}
                dispatch={dispatch}
                date={date}
                onSeatSelect={handleSeatSelect}
              />
            </div>

            {Object.keys(selectedSeats).length > 0 && (
              <Button
                newStyles={styles.button}
                onClickFunc={handleAddToCart}
                disabled={isProcessingBasket}
              >
                <span>
                  {isProcessingBasket ? "Обработка..." : "Добавить в корзину"}
                </span>
              </Button>
            )}
          </div>
          
          {isOpenModalNoSelectedSeats ? (
            <Modal
              title={ModalTexts.courtRent.errorNoSelectedSeats.TITLE}
              subTitle={ModalTexts.courtRent.errorNoSelectedSeats.SUB_TITLE}
              isOpen={isOpenModalNoSelectedSeats}
              setIsOpen={setIsOpenModalNoSelectedSeats}
            >
              <Button newStyles={styles.button} onClickFunc={handleCloseModalNoSeats}>
                Назад
              </Button>
            </Modal>
          ) : null}
          
          {isOpenModalUnavailable ? (
            <Modal
              title={ModalTexts.courtRent.errorUnAvailable.TITLE}
              subTitle={ModalTexts.courtRent.errorUnAvailable.SUB_TITLE}
              isOpen={isOpenModalUnavailable}
              setIsOpen={setIsOpenModalUnavailable}
            >
              <Button newStyles={styles.button} onClickFunc={handleCloseModalUnavailable}>
                Назад
              </Button>
            </Modal>
          ) : null}
          
          <PromoModals
            showWeekdayPromoModal={showWeekdayPromoModal}
            showWeekendPromoModal1={showWeekendPromoModal1}
            showWeekendPromoModal2={showWeekendPromoModal2}
            onCloseWeekday={() => setShowWeekdayPromoModal(false)}
            onCloseWeekend1={() => setShowWeekendPromoModal1(false)}
            onCloseWeekend2={() => setShowWeekendPromoModal2(false)}
          />
        </section>
      </main>
    </>
  );
};